import React, { useState, useMemo } from 'react';
import '../../pages/Kpi/kpi.css';
import { Chart } from 'react-google-charts';
import Toggle from '../common/Toggle';

/**
 * Props interface for ConfigurableBarChart component
 * @interface ConfigurableBarChartProps
 * @property {any[]} filteredData - Array of data records to display in chart, filtered by date/producer
 */
interface ConfigurableBarChartProps {
	filteredData: any[];
}

/**
 * Mapping of database field names to user-friendly display names
 * Used to convert technical field names into readable chart labels
 * Only fields included in this map will be available for chart configuration
 */
const friendlyNameMap: Record<string, string> = {
		bsw: 'BS&W',
		gross_barrels: 'Gross Barrels',
		net_barrels: 'Net Barrels',
		producer: 'Producer',
		load_date: 'Load Date',
		api_gravity: 'API Gravity',
		tank_temp: 'Tank Temp',
		approved_mileage: 'Standard Mileage',
		mileage:'Charged Mileage',
		observed_gravity: 'Observed Gravity',
		observed_temperature: 'Observed Temperature',
		approved_rate: 'Approved Rate',
};

/**
 * Returns user-friendly display name for a database field.
 * If a mapping exists, returns the friendly name; otherwise, returns the original key.
 * @param key - Database field name
 * @returns Friendly name or original key
 */
function displayName(key: string): string {
	return friendlyNameMap[key] || key;
}

/**
 * Available options for X-axis grouping in the chart
 * Each option represents a different way to categorize and group the data
 * Users can switch between these to view data from different perspectives
 */
const xAxisOptions = [
	{ key: 'load_date', label: 'Date' },
	{ key: 'producer', label: 'Producer' },
	{ key: 'lease', label: 'Location' },
	{ key: 'destination', label: 'Destination' },
	{ key: 'market', label: 'Market' },
	{ key: 'driver', label: 'Driver' },
	{ key: 'carrier', label: 'Carrier' },
];

/**
 * Extracts available numeric fields from the dataset for Y-axis selection
 * Only includes fields that:
 * 1. Have a friendly name mapping
 * 2. Contain numeric values in at least one record
 * 3. Are not null or empty
 * 
 * @param data - Array of data records to analyze
 * @returns Array of field names that can be used for Y-axis metrics
 */
function getAllKeys(data: any[]): string[] {
	if (!data || data.length === 0) return [];
	// Get unique keys from all objects in the dataset
	const keys = Array.from(new Set(data.flatMap(obj => Object.keys(obj))));
	// Filter to only numeric fields with friendly names
	return keys.filter(key => {
		// Must have a friendly name mapping
		if (displayName(key) === key) return false;
		// Must have at least one numeric value
		return data.some(obj => {
			const v = obj[key];
			if (v === null || v === '') return false;
			return typeof v === 'number' || !isNaN(Number(v));
		});
	});
}

/**
 * ConfigurableBarChart Component
 * 
 * Renders an interactive bar chart with dual y-axes and extensive customization options:
 * - Drag-and-drop axis configuration
 * - Statistical aggregation options (Average, Min, Max, Sum, StdDev)
 * - Dual y-axes with independent scales and toggle controls
 * - Multiple grouping options (by producer, date, location, etc.)
 * - Sorting controls for data presentation
 * 
 * @param filteredData - Array of data records filtered by month/year and other criteria
 */
const ConfigurableBarChart: React.FC<ConfigurableBarChartProps> = ({ filteredData }) => {
	// Available numeric fields for Y-axis selection, computed from actual data
	const numericKeys = useMemo(() => getAllKeys(filteredData), [filteredData]);
	
	// Chart axis configuration state
	const [xKey, setXKey] = useState<string>('producer');          // X-axis grouping field
	const [yKey, setYKey] = useState<string>('bsw');              // Primary Y-axis field
	const [y2Key, setY2Key] = useState<string>('gross_barrels');  // Secondary Y-axis field
	const [yEnabled, setYEnabled] = useState<boolean>(true);      // Primary Y-axis visibility
	const [y2Enabled, setY2Enabled] = useState<boolean>(true);    // Secondary Y-axis visibility
	
	// Statistical aggregation options for data grouping
	const statOptions = ['Average','Standard Deviation','Min','Max','Sum'] as const;
	type StatOption = typeof statOptions[number];
	const [yStat, setYStat] = useState<StatOption>('Average');    // Primary axis statistic
	const [y2Stat, setY2Stat] = useState<StatOption>('Average');  // Secondary axis statistic
	
	// Data sorting configuration
	const sortModes = ['Alphabetical','Largest to Smallest'] as const;
	type SortMode = typeof sortModes[number];
	const [sortMode, setSortMode] = useState<SortMode>('Alphabetical');

	/**
	 * Effect to auto-correct Y-axis selections when data changes
	 * Ensures selected fields are always available in the current dataset
	 * Provides fallback selections to prevent chart errors
	 */
	React.useEffect(() => {
		if (numericKeys.length === 0) return;
		
		// Reset primary Y-axis if current selection is invalid
		if (!numericKeys.includes(yKey)) {
			setYKey(numericKeys[0]);
		}
		
		// Reset secondary Y-axis if current selection is invalid
		if (!numericKeys.includes(y2Key)) {
			const alt = numericKeys.find(k => k !== yKey);
			setY2Key(alt || numericKeys[0]);
		}
	}, [numericKeys]);

	/**
	 * Memoized data grouping by X-axis field
	 * Groups all data points by the selected X-axis category and separates Y1 and Y2 values
	 * Handles missing/null values by assigning to 'Unknown' category
	 * Only processes values for enabled axes to optimize performance
	 */
	const groupedValues = useMemo(() => {
		const map: Record<string, { y1: number[]; y2: number[] }> = {};
		
		filteredData.forEach(item => {
			// Group by X-axis field (e.g., producer, date, location)
			const xVal = item[xKey] ?? 'Unknown';
			if (!map[xVal]) map[xVal] = { y1: [], y2: [] };
			
			// Collect Y1 values if primary axis is enabled
			if (yEnabled) {
				const v1 = Number(item[yKey]);
				if (!isNaN(v1)) map[xVal].y1.push(v1);
			}
			
			// Collect Y2 values if secondary axis is enabled
			if (y2Enabled) {
				const v2 = Number(item[y2Key]);
				if (!isNaN(v2)) map[xVal].y2.push(v2);
			}
		});
		return map;
	}, [filteredData, xKey, yKey, y2Key, yEnabled, y2Enabled]);

	/**
	 * Memoized statistical calculations for primary Y-axis
	 * Applies selected statistic (Average, Min, Max, Sum, StdDev) to each group's Y1 values
	 */
	const y1StatValues = useMemo(() => {
		if (!yEnabled) return [] as number[];
		return Object.values(groupedValues).map(g => computeStat(g.y1, yStat)).filter(v => !isNaN(v));
	}, [groupedValues, yEnabled, yStat]);
	
	/**
	 * Memoized statistical calculations for secondary Y-axis
	 * Applies selected statistic to each group's Y2 values independently
	 */
	const y2StatValues = useMemo(() => {
		if (!y2Enabled) return [] as number[];
		return Object.values(groupedValues).map(g => computeStat(g.y2, y2Stat)).filter(v => !isNaN(v));
	}, [groupedValues, y2Enabled, y2Stat]);

	// Primary Y-axis scale calculation
	const y1Min = 0;
	const y1Max = y1StatValues.length ? Math.max(...y1StatValues) : 10;
	
	/**
	 * Memoized tick marks for primary Y-axis
	 * Generates appropriate scale divisions based on data range
	 * Uses fixed ticks for small values, calculated steps for larger ranges
	 */
	const y1Ticks = useMemo(() => {
		if (!yEnabled) return [] as number[];
		if (y1Max <= 10) return [0, 1, 2, 5, 10];
		const step = Math.max(1, Math.round((y1Max - y1Min) / 5));
		return Array.from({ length: 6 }, (_, i) => y1Min + i * step);
	}, [y1Max, yEnabled]);

	// Secondary Y-axis scale calculation
	const y2Min = 0;
	const y2Max = y2StatValues.length ? Math.max(...y2StatValues) : 10;
	
	/**
	 * Memoized tick marks for secondary Y-axis
	 * Independent scaling from primary axis to accommodate different value ranges
	 */
	const y2Ticks = useMemo(() => {
		if (!y2Enabled) return [] as number[];
		if (y2Max <= 10) return [0, 1, 2, 5, 10];
		const step = Math.max(1, Math.round((y2Max - y2Min) / 5));
		return Array.from({ length: 6 }, (_, i) => y2Min + i * step);
	}, [y2Max, y2Enabled]);

	// Drag-and-drop state management for axis configuration
	const [draggedKey, setDraggedKey] = useState<string | null>(null);
	const [dragTarget, setDragTarget] = useState<'y1' | 'y2' | null>(null);

	/**
	 * Drag-and-drop handlers for intuitive axis configuration
	 * Allows users to drag fields from the list to Y1 or Y2 axis slots
	 */
	const handleDragStart = (key: string) => setDraggedKey(key);
	
	/** Handler for dropping a field onto the primary Y-axis */
	const handleDropY1 = () => {
		if (draggedKey) {
			setYKey(draggedKey);
			if (!yEnabled) setYEnabled(true);  // Auto-enable axis when field is assigned
		}
		setDraggedKey(null);
		setDragTarget(null);
	};
	
	/** Handler for dropping a field onto the secondary Y-axis */
	const handleDropY2 = () => {
		if (draggedKey) {
			setY2Key(draggedKey);
			if (!y2Enabled) setY2Enabled(true);  // Auto-enable axis when field is assigned
		}
		setDraggedKey(null);
		setDragTarget(null);
	};

	/**
	 * Computes statistical aggregation for a group of values
	 * Supports multiple statistical operations for data analysis
	 * 
	 * @param values - Array of numeric values to aggregate
	 * @param stat - Statistical operation to perform
	 * @returns Computed statistical value, rounded to 3 decimal places
	 */
	function computeStat(values: number[], stat: StatOption): number {
		if (values.length === 0) return 0;
		
		switch (stat) {
			case 'Average':
				return Number((values.reduce((a,b)=>a+b,0)/values.length).toFixed(3));
			case 'Standard Deviation': {
				// Calculate population standard deviation
				const mean = values.reduce((a,b)=>a+b,0)/values.length;
				const variance = values.reduce((a,b)=>a + Math.pow(b-mean,2),0)/values.length;
				return Number(Math.sqrt(variance).toFixed(3));
			}
			case 'Min':
				return Math.min(...values);
			case 'Max':
				return Math.max(...values);
			case 'Sum':
				return values.reduce((a,b)=>a+b,0);
			default:
				return 0;
		}
	}

	/**
	 * Memoized chart data generation for Google Charts
	 * Transforms filtered data into chart-ready format with:
	 * - Dynamic column headers based on selected fields and statistics
	 * - Grouped data by X-axis field
	 * - Statistical aggregation for each group
	 * - Sorting based on user preference
	 */
	const chartData = useMemo(() => {
		// Build dynamic header row
		const header: string[] = [displayName(xKey)];
		if (yEnabled) header.push(`${yStat} ${displayName(yKey)}`);
		if (y2Enabled) header.push(`${y2Stat} ${displayName(y2Key)}`);
		
		// Handle empty dataset
		if (!filteredData || filteredData.length === 0) {
			const emptyNumericCols = Array(header.length - 1).fill(0);
			return [header, [0, ...emptyNumericCols]];
		}
		
		// Group raw data by X-axis field
		const groups: Record<string, { y1: number[]; y2: number[] }> = {};
		filteredData.forEach(item => {
			const xVal = item[xKey] ?? 'Unknown';
			if (!groups[xVal]) groups[xVal] = { y1: [], y2: [] };
			
			// Collect Y1 values for statistical calculation
			if (yEnabled) {
				const yVal = Number(item[yKey]);
				if (!isNaN(yVal)) groups[xVal].y1.push(yVal);
			}
			
			// Collect Y2 values for statistical calculation
			if (y2Enabled) {
				const y2Val = Number(item[y2Key]);
				if (!isNaN(y2Val)) groups[xVal].y2.push(y2Val);
			}
		});
		
		// Convert grouped data to chart rows with statistical aggregation
		let dataRows = Object.entries(groups).map(([x, vals]) => {
			const row: (string | number)[] = [x];
			if (yEnabled) row.push(computeStat(vals.y1, yStat));
			if (y2Enabled) row.push(computeStat(vals.y2, y2Stat));
			return row;
		});
		
		// Apply user-selected sorting
		if (sortMode === 'Alphabetical') {
			dataRows.sort((a,b) => String(a[0]).localeCompare(String(b[0])));
		} else if (sortMode === 'Largest to Smallest') {
			// Sort by the first enabled Y-axis value
			const valueIndex = yEnabled ? 1 : (y2Enabled ? 1 : -1);
			if (valueIndex !== -1) {
				dataRows.sort((a,b) => Number(b[valueIndex]) - Number(a[valueIndex]));
			}
		}
		
		return [header, ...dataRows];
	}, [filteredData, xKey, yKey, y2Key, yEnabled, y2Enabled, yStat, y2Stat, sortMode]);

	/**
	 * Memoized chart configuration options for Google Charts
	 * Dynamically generates title, axis configurations, and dual y-axis setup
	 * Adapts to enabled/disabled axes and field selections
	 */
	const chartOptions = useMemo(() => {
		// Build dynamic chart title from selected fields
		const parts: string[] = [displayName(xKey)];
		if (yEnabled) parts.push(`${yStat} ${displayName(yKey)}`);
		if (y2Enabled) parts.push(`${y2Stat} ${displayName(y2Key)}`);
		const title = parts.join(' vs ');

		// Configure Y-axes based on enabled state
		let vAxes: any = {};
		if (yEnabled && y2Enabled) {
			// Dual Y-axis configuration with independent scales and styling
			vAxes = {
				0: {  // Primary Y-axis (left)
					title: `${yStat} ${displayName(yKey)}`,
					titleTextStyle: { color: '#2973A6', fontSize: 16, bold: true },
					textStyle: { color: '#2973A6', fontSize: 14 },
					viewWindowMode: 'explicit',
					viewWindow: { min: y1Min, max: y1Max },
					gridlines: { color: '#eee' },
					ticks: y1Ticks,
				},
				1: {  // Secondary Y-axis (right)
					title: `${y2Stat} ${displayName(y2Key)}`,
					titleTextStyle: { color: '#A62973', fontSize: 16, bold: true },
					textStyle: { color: '#A62973', fontSize: 14 },
					viewWindowMode: 'explicit',
					viewWindow: { min: y2Min, max: y2Max },
					gridlines: { color: '#f3e6f0' },
					format: 'decimal',
					ticks: y2Ticks,
				},
			};
		} else if (yEnabled) {
			// Single primary Y-axis configuration
			vAxes = {
				0: {
					title: `${yStat} ${displayName(yKey)}`,
					titleTextStyle: { color: '#2973A6', fontSize: 16, bold: true },
					textStyle: { color: '#2973A6', fontSize: 14 },
					viewWindowMode: 'explicit',
					viewWindow: { min: y1Min, max: y1Max },
					gridlines: { color: '#eee' },
					ticks: y1Ticks,
				},
			};
		} else if (y2Enabled) {
			// Single secondary Y-axis configuration
			vAxes = {
				0: {
					title: `${y2Stat} ${displayName(y2Key)}`,
					titleTextStyle: { color: '#A62973', fontSize: 16, bold: true },
					textStyle: { color: '#A62973', fontSize: 14 },
					viewWindowMode: 'explicit',
					viewWindow: { min: y2Min, max: y2Max },
					gridlines: { color: '#f3e6f0' },
					format: 'decimal',
					ticks: y2Ticks,
				},
			};
		}

		// Configure data series colors and axis assignments
		const series: any = {};
		if (yEnabled && y2Enabled) {
			// Dual series: assign to different axes with distinct colors
			series[0] = { targetAxisIndex: 0, color: '#2973A6' };  // Primary axis - blue
			series[1] = { targetAxisIndex: 1, color: '#A62973' };  // Secondary axis - purple
		} else if (yEnabled) {
			series[0] = { targetAxisIndex: 0, color: '#2973A6' };  // Single primary series
		} else if (y2Enabled) {
			series[0] = { targetAxisIndex: 0, color: '#A62973' };  // Single secondary series
		}

		return {
			title,
			legend: 'top',
			axisTitlesPosition: 'out',
			vAxes,
			series,
			hAxis: { 
				title: displayName(xKey), 
				slantedText: false, 
				slantedTextAngle: 45,
				textStyle: { fontSize: 16, bold: true, color: '#222' },
				titleTextStyle: { fontSize: 16, bold: true, color: '#222' }
			},
			bar: { groupWidth: '30%' },
			chartArea: { left: 80, right: 100, top: 40, bottom: 80 },
			annotations: { alwaysOutside: true, textStyle: { fontSize: 12, bold: true, color: '#000' } },
		};
	}, [xKey, yKey, y2Key, y2Min, y2Max, y2Ticks, yEnabled, y2Enabled, yStat, y2Stat]);

	return (
		<div className='kpi_outerContainer'>
			{/* Configuration Controls Panel */}
			<div className='kpi_controlsContainer'>
				<div className='kpi_sortToggleWrapper'>
					<span className='kpi_label'>Sort:</span>
					<Toggle
						checked={sortMode === 'Largest to Smallest'}
						onChange={(checked) => setSortMode(checked ? 'Largest to Smallest' : 'Alphabetical')}
						labelOn='Largest → Smallest'
						labelOff='Alphabetical'
					/>
				</div>
				<div>
					<label className='kpi_label'>X-Axis:</label>
					<select value={xKey} onChange={e => setXKey(e.target.value)} className='kpi_select'>
						{xAxisOptions.map(opt => (
							<option key={opt.key} value={opt.key}>{opt.label}</option>
						))}
					</select>
				</div>
				{/* Primary Y-Axis Configuration */}
				<div>
					<label className='kpi_label'>Y-Axis (drag variable here):</label>
					<div className='kpi_axisRow'>
						{/* Drop zone for primary Y-axis field selection */}
						<div
							onDrop={handleDropY1}
							onDragOver={e => { e.preventDefault(); setDragTarget('y1'); }}
							className={`kpi_dropZone ${dragTarget==='y1' ? 'kpi_dropZoneActive' : 'kpi_dropZoneInactive'}`}
						>
							{yEnabled ? displayName(yKey) : '(none)'}
						</div>
						{/* Statistical operation selector for primary axis */}
						{yEnabled && (
							<select value={yStat} onChange={e => setYStat(e.target.value as StatOption)} className='kpi_statSelect'>
								{statOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
							</select>
						)}
						{/* Toggle button for enabling/disabling primary axis */}
						<button
							type='button'
							onClick={() => setYEnabled(prev => !prev)}
							disabled={yEnabled && !y2Enabled}
							className={`kpi_axisBtn ${yEnabled ? 'kpi_axisBtnRemovePrimary' : 'kpi_axisBtnAddPrimary'} ${yEnabled && !y2Enabled ? 'kpi_axisBtnDisabled' : ''}`}
							aria-label={yEnabled ? 'Remove primary y-axis variable' : 'Add primary y-axis variable'}
						>{yEnabled ? '×' : 'Add'}</button>
					</div>
				</div>
				{/* Secondary Y-Axis Configuration */}
				<div>
					<label className='kpi_label'>Secondary Y-Axis (drag variable here):</label>
					<div className='kpi_axisRow'>
						{/* Drop zone for secondary Y-axis field selection */}
						<div
							onDrop={handleDropY2}
							onDragOver={e => { e.preventDefault(); setDragTarget('y2'); }}
							className={`kpi_dropZone ${dragTarget==='y2' ? 'kpi_dropZoneActive' : 'kpi_dropZoneInactive'}`}
						>
							{y2Enabled ? displayName(y2Key) : '(none)'}
						</div>
						{/* Statistical operation selector for secondary axis */}
						{y2Enabled && (
							<select value={y2Stat} onChange={e => setY2Stat(e.target.value as StatOption)} className='kpi_statSelect'>
								{statOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
							</select>
						)}
						{/* Toggle button for enabling/disabling secondary axis */}
						<button
							type='button'
							onClick={() => setY2Enabled(prev => !prev)}
							disabled={y2Enabled && !yEnabled}
							className={`kpi_axisBtn ${y2Enabled ? 'kpi_axisBtnRemoveSecondary' : 'kpi_axisBtnAddSecondary'} ${y2Enabled && !yEnabled ? 'kpi_axisBtnDisabled' : ''}`}
							aria-label={y2Enabled ? 'Remove secondary y-axis variable' : 'Add secondary y-axis variable'}
						>{y2Enabled ? '×' : 'Add'}</button>
					</div>
				</div>
			</div>
			
			{/* Draggable Field Selection Chips */}
			<div className='kpi_chipContainer'>
				{/* Generate draggable chips for each available numeric field */}
				{numericKeys.map(key => (
					<div
						key={key}
						draggable
						onDragStart={() => handleDragStart(key)}
						className='kpi_variableChip'
					>
						{displayName(key)}
					</div>
				))}
			</div>
			
			{/* Main Chart Display */}
			<Chart
				chartType='ColumnChart'
				width='100%'
				height='35rem'
				data={chartData}
				options={chartOptions}
			/>
		</div>
	);
};

export default ConfigurableBarChart;
